import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, jobTrackerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * AuthCallback Component
 * Handles OAuth redirects from both:
 * 1. Custom Google OAuth (AlignifyNOW) - receives ?code=xxx
 * 2. Emergent Auth (legacy) - receives #session_id=xxx
 * 
 * Promo Code Support:
 * - Reads promo_code from localStorage (set by landing page with ?promo=RESET5)
 * - Passes to backend during registration for bonus credits
 * 
 * Smart Redirect Logic:
 * - If login_redirect is 'match_score', return to home page
 * - If user has 0 applications, redirect to home page (new user)
 * - If user has 1+ applications, redirect to tracker (established user)
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Initializing...');
  const [error, setError] = useState(null);

  // Determine smart redirect destination
  const getSmartRedirect = async () => {
    // Check if user came from "Scan" button - fastest path
    const loginRedirect = localStorage.getItem('login_redirect');
    localStorage.removeItem('login_redirect'); // Clear after reading
    
    if (loginRedirect === 'match_score') {
      return '/match'; // Return to match score page to finish scanning
    }
    
    // For speed, default to tracker for returning users
    // The tracker will show the appropriate view based on their data
    return '/tracker';
  };

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Check for custom Google OAuth code (query param)
        const code = searchParams.get('code');
        
        // Check for promo code (stored by landing page or passed in state)
        const promoCode = localStorage.getItem('promo_code') || searchParams.get('state');
        if (promoCode) {
          console.log('AuthCallback - Promo code found:', promoCode);
          localStorage.removeItem('promo_code'); // Clear after reading
        }
        
        // Check for Emergent Auth session_id (URL hash)
        const hash = window.location.hash;
        const hashParams = new URLSearchParams(hash.substring(1));
        const sessionId = hashParams.get('session_id');

        console.log('AuthCallback - Code:', code ? code.substring(0, 20) + '...' : 'None');
        console.log('AuthCallback - Session ID:', sessionId ? sessionId.substring(0, 20) + '...' : 'None');

        if (code) {
          // Custom Google OAuth flow (AlignifyNOW branded)
          setStatus('Processing Google sign-in...');
          
          const redirectUri = window.location.origin + '/auth/callback';
          // Pass promo code for new user registration bonus
          const response = await authAPI.exchangeGoogleCode(code, redirectUri, promoCode);
          
          console.log('AuthCallback - Exchange response success:', response.success);

          if (response.success && response.token) {
            setStatus('Saving authentication...');
            localStorage.setItem('auth_token', response.token);
            
            // Clear URL params
            window.history.replaceState(null, '', '/auth/callback');
            
            setStatus('Success! Redirecting...');
            await checkAuth();
            
            // Smart redirect based on user status
            const redirectTo = await getSmartRedirect();
            navigate(redirectTo, { replace: true });
          } else {
            throw new Error(response.message || 'No token received');
          }
          
        } else if (sessionId) {
          // Emergent Auth flow (legacy)
          setStatus('Processing sign-in...');
          
          const response = await authAPI.exchangeGoogleSession(sessionId);
          console.log('AuthCallback - Exchange response success:', response.success);

          if (response.success && response.token) {
            setStatus('Saving authentication...');
            localStorage.setItem('auth_token', response.token);
            
            // Clear URL hash
            window.history.replaceState(null, '', '/auth/callback');
            
            setStatus('Success! Redirecting...');
            await checkAuth();
            
            // Wait a moment for auth state to update
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Smart redirect based on user status
            const redirectTo = await getSmartRedirect();
            navigate(redirectTo, { replace: true });
          } else {
            throw new Error(response.message || 'No token received');
          }
          
        } else {
          // No auth data found
          setError('No authentication data received. Please try again.');
          setTimeout(() => navigate('/', { replace: true }), 3000);
        }
        
      } catch (err) {
        console.error('AuthCallback error:', err);
        setError(err.message || 'Authentication failed. Please try again.');
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    processAuth();
  }, [navigate, checkAuth, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-purple-50">
      <div className="text-center max-w-md mx-auto px-4">
        {error ? (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium mb-2">{error}</p>
            <p className="text-gray-500 text-sm">Redirecting back...</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium mb-2">{status}</p>
            <p className="text-gray-500 text-sm">Please wait...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
